import { useCallback, useRef, useState } from 'react'
import { BaseHTTP } from '../api/base.http'
import { markBukSyncPending, clearBukSyncPending } from '../utils/bukSyncFlag'

export const useBukSyncStatus = businessId => {
  const [syncStatus, setSyncStatus] = useState('idle')
  const [progress, setProgress] = useState(0)
  const [totalMonths, setTotalMonths] = useState(0)
  const [completedMonths, setCompletedMonths] = useState(0)
  const [months, setMonths] = useState([])
  const [error, setError] = useState('')
  const pollingRef = useRef(null)
  const pollingAttemptsRef = useRef(0)

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearTimeout(pollingRef.current)
      pollingRef.current = null
    }
    pollingAttemptsRef.current = 0
  }, [])

  const resetSyncState = useCallback(() => {
    stopPolling()
    setSyncStatus('idle')
    setProgress(0)
    setTotalMonths(0)
    setCompletedMonths(0)
    setMonths([])
    setError('')
  }, [stopPolling])

  const checkStatusOnce = useCallback(
    async ({ onCompleted, onError } = {}) => {
      if (!businessId) return null

      try {
        const http = BaseHTTP.getInstance().http
        const response = await http.get(`v1/business/${businessId}/integrations/buk/status`)
        const data = response.data

        setSyncStatus(data.status)
        if (data.progress !== undefined) setProgress(data.progress)
        if (data.totalMonths !== undefined) setTotalMonths(data.totalMonths)
        if (data.completedMonths !== undefined) setCompletedMonths(data.completedMonths)
        if (Array.isArray(data.months)) setMonths(data.months)

        if (data.status === 'processing') {
          markBukSyncPending(businessId)
        } else if (data.status === 'completed') {
          stopPolling()
          clearBukSyncPending(businessId)
          onCompleted?.(data)
        } else if (data.status === 'error') {
          stopPolling()
          clearBukSyncPending(businessId)
          setError(data.error || 'Error durante la sincronización con Buk')
          onError?.(data)
        }

        return data
      } catch (err) {
        console.error('Error checking Buk sync status:', err)
        return null
      }
    },
    [businessId, stopPolling]
  )

  const startPolling = useCallback(
    (callbacks) => {
      stopPolling()
      pollingAttemptsRef.current = 0

      const poll = () => {
        pollingAttemptsRef.current += 1
        const baseInterval = 3000
        const maxInterval = 10000
        const backoffMultiplier = 1.2
        const interval = Math.min(
          baseInterval * Math.pow(backoffMultiplier, pollingAttemptsRef.current - 1),
          maxInterval
        )

        checkStatusOnce(callbacks)

        if (pollingRef.current) {
          clearTimeout(pollingRef.current)
        }
        pollingRef.current = setTimeout(poll, interval)
      }

      poll()
    },
    [checkStatusOnce, stopPolling]
  )

  return {
    syncStatus,
    progress,
    totalMonths,
    completedMonths,
    months,
    error,
    setSyncStatus,
    setProgress,
    setTotalMonths,
    setCompletedMonths,
    setMonths,
    setError,
    startPolling,
    stopPolling,
    resetSyncState,
    checkStatusOnce
  }
}
